import Employee from "@/model/Employee";
import Worklog from "@/model/Worklog";
import {WorklogType} from "@/enums";
import {OrderWorklogV3Dto} from "@/service/OrderServiceV2";
import {groupBy} from "@/Utils";

export class OrderWorklogsDate {
    public date!: string;
    public employees!: Array<OrderWorklogsDateEmployee>;
    public employeeCount!: number;
    public start!: string;
    public end!: string;
    public minutes!: Array<Minutes>;
}

export class OrderWorklogsDateEmployee {
    public employee!: Employee;
    public worklogs!: Array<Worklog>;
    public start!: string;
    public end!: string;
    public minutes!: Array<Minutes>;
}

export class Minutes {
    public worklogType!: WorklogType;
    public minutes!: number;
}

/**
 * Groups the worklogs of an order, as GET /v3/orders/{orderId}/worklogs returns them, by day and, within each day, by
 * employee. Days come oldest first, employees by last and first name, and each employee's worklogs by start time. Each
 * day and employee carries the earliest start, the latest end and the minutes per worklog type of its worklogs.
 */
export function groupOrderWorklogs(worklogs: Array<OrderWorklogV3Dto>): Array<OrderWorklogsDate> {
  return groupInto(worklogs, worklog => worklog.date)
    .map(dayWorklogs => {
      const employees = groupInto(dayWorklogs, worklog => worklog.employee.id)
        .map(toOrderWorklogsDateEmployee)
        .sort((a, b) => compareNames(a.employee, b.employee));
      return Object.assign(new OrderWorklogsDate(), {
        date: dayWorklogs[0].date,
        employees: employees,
        employeeCount: employees.length,
        start: earliest(employees.map(employee => employee.start)),
        end: latest(employees.map(employee => employee.end)),
        minutes: sumMinutes(employees.flatMap(employee => employee.minutes))
      });
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

function toOrderWorklogsDateEmployee(worklogs: Array<OrderWorklogV3Dto>): OrderWorklogsDateEmployee {
  const sorted = [...worklogs].sort((a, b) => a.start.localeCompare(b.start));
  return Object.assign(new OrderWorklogsDateEmployee(), {
    employee: toEmployee(sorted[0].employee),
    worklogs: sorted.map(toWorklog),
    start: earliest(sorted.map(worklog => worklog.start)),
    end: latest(sorted.map(worklog => worklog.end)),
    minutes: sumMinutes(sorted.map(worklog => ({worklogType: worklog.type, minutes: worklog.minutes})))
  });
}

// The employee number is the `employeeId` the rest of the app compares the signed-in employee with
function toEmployee(employee: OrderWorklogV3Dto["employee"]): Employee {
  return Object.assign(new Employee(), {
    uuid: employee.id,
    employeeId: employee.employeeNumber,
    firstName: employee.firstName,
    lastName: employee.lastName
  });
}

function toWorklog(worklog: OrderWorklogV3Dto): Worklog {
  return Object.assign(new Worklog(), {
    uuid: worklog.id,
    date: worklog.date,
    start: worklog.start,
    end: worklog.end,
    worklogType: worklog.type,
    comment: worklog.comment,
    createdBy: worklog.createdBy,
    createdAt: worklog.createdAt
  });
}

function groupInto<T>(items: Array<T>, key: (item: T) => string): Array<Array<T>> {
  const groups = groupBy(items, key, (group: Array<T> | null | undefined, item: T) => (group ?? []).concat(item));
  return Array.from(groups.values());
}

function compareNames(a: Employee, b: Employee): number {
  return (a.lastName ?? "").localeCompare(b.lastName ?? "") || (a.firstName ?? "").localeCompare(b.firstName ?? "");
}

// Times of day in the form HH:mm compare like strings
function earliest(times: Array<string>): string {
  return times.reduce((a, b) => (b < a ? b : a));
}

function latest(times: Array<string>): string {
  return times.reduce((a, b) => (b > a ? b : a));
}

function sumMinutes(minutes: Array<Minutes>): Array<Minutes> {
  const sums = new Map<WorklogType, number>();
  minutes.forEach(entry => sums.set(entry.worklogType, (sums.get(entry.worklogType) ?? 0) + entry.minutes));
  return Array.from(sums, ([worklogType, sum]) => Object.assign(new Minutes(), {worklogType: worklogType, minutes: sum}));
}
